import React from 'react';
import { Link } from 'react-router-dom';

const values = [
  {
    title: 'Compassion',
    description: 'We believe in treating everyone with respect, empathy, and care — building communities grounded in kindness.',
    icon: (
      <svg className="h-7 w-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
  },
  {
    title: 'Empowerment',
    description: 'Our initiatives equip individuals with resources and skills that enable them to reach their full potential.',
    icon: (
      <svg className="h-7 w-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    title: 'Community',
    description: 'We are committed to building supportive and inclusive communities that uplift and empower each other.',
    icon: (
      <svg className="h-7 w-7 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

function About() {
  return (
    <div>
      {/* Page header */}
      <div className="bg-gray-900 text-white py-20 px-4 text-center">
        <span className="inline-block text-red-400 text-sm font-semibold uppercase tracking-widest mb-4">Who We Are</span>
        <h1 className="text-4xl md:text-6xl font-black mb-4 leading-tight">About Khisa Foundation</h1>
        <p className="text-gray-400 text-lg max-w-xl mx-auto leading-relaxed">
          Established to create a world where everyone has access to essential resources, support, and opportunity.
        </p>
      </div>

      {/* Mission & Vision */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-red-600 text-sm font-semibold uppercase tracking-widest mb-3">Our Mission</span>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight mb-5">
                Empowering individuals to shape a brighter future
              </h2>
              <p className="text-gray-500 leading-relaxed">
                The Khisa Foundation for Impact and Empowerment was established with the goal of creating a world where everyone has access to essential resources, support, and opportunities. We are dedicated to empowering individuals and communities through a range of impactful programs — from pad drives to digital literacy.
              </p>
              <Link to="/get-involved"
                className="inline-flex items-center gap-2 mt-8 bg-red-600 hover:bg-red-700 text-white font-semibold px-7 py-3 rounded-full transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5">
                Get Involved
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src="/assets/khisa5.jpg" alt="Our mission" className="w-full h-80 object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-red-600 text-white rounded-2xl p-5 shadow-xl">
                <p className="text-3xl font-black">6000+</p>
                <p className="text-red-200 text-sm">Lives Impacted</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <span className="inline-block text-red-600 text-sm font-semibold uppercase tracking-widest mb-4">Our Vision</span>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
            A world of equality and opportunity
          </h2>
          <p className="text-gray-500 text-lg leading-relaxed">
            Our vision is a world where every individual has the tools to succeed and the support to thrive. We aim to inspire and empower through education, mentorship, and community support — fostering a loving world of care and compassion.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block text-red-600 text-sm font-semibold uppercase tracking-widest mb-3">What Guides Us</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {values.map(({ title, description, icon }) => (
              <div key={title}
                className="group bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-center">
                <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:bg-red-100 transition-colors">
                  {icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
